import React, { useEffect, useRef, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { usePhonePresence } from '../../../core/telephony/usePhonePresence';

// The agent's own phone link, always in sight: green when Daksha Caller is
// connected, amber when it is up but something will hurt calls, red when the
// system cannot reach the phone — with the reason, so nobody discovers it by
// clicking "Call" and waiting.
//
// Hidden for users who have never connected a phone (most office staff).

const TONE = {
    online: { dot: 'bg-green-500', box: 'border-green-200 bg-green-50 text-green-800' },
    warning: { dot: 'bg-amber-500', box: 'border-amber-200 bg-amber-50 text-amber-800' },
    offline: { dot: 'bg-red-500 animate-pulse', box: 'border-red-200 bg-red-50 text-red-700' },
};

export const PhoneReasons = ({ view }) => (
    <ul className="space-y-1">
        {view.reasons.map(r => (
            <li key={r.code} className={`text-xs leading-snug ${r.blocks ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>• {r.text}</li>
        ))}
    </ul>
);

const PhoneStatusPill = ({ uid, compact = false }) => {
    const { view } = usePhonePresence(uid);
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    if (!view || view.status === 'none') return null;
    const tone = TONE[view.status] || TONE.offline;
    const label = view.status === 'online' ? 'Phone connected' : view.headline;

    return (
        <div className="relative" ref={boxRef}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                title={label}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold max-w-[260px] ${tone.box}`}
            >
                <span className={`h-2 w-2 rounded-full shrink-0 ${tone.dot}`} />
                <Smartphone size={13} className="shrink-0" />
                {!compact && <span className="truncate">{label}</span>}
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-xl p-3 z-[120] space-y-2">
                    <div className="text-sm font-bold text-slate-800">{label}</div>
                    {view.reasons.length > 0
                        ? <PhoneReasons view={view} />
                        : <div className="text-xs text-slate-500">Daksha Caller is running, online and ready — calls from the system will ring on your phone.</div>}
                    {view.status !== 'online' && (
                        <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2">
                            On the phone: open Daksha Caller → More → Phone connection, and follow the red items there.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PhoneStatusPill;
